/**
 * The Admin Portal screens — SRS §9.3 / screens 18–25. Seats and expiry,
 * clubs (create, archive with confirmation, 10-day restore, member
 * notification), the members summary across clubs, subscription & renewal,
 * and the portal's own trace log.
 */

import { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { platform } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatMoney, paymentMethods } from '../../data';
import { ConsoleShell } from '../../components/ConsoleShell';
import { CopyRow } from '../../components/PaymentPanel';
import { ProofPicker } from '../../components/ProofPicker';
import { useBackToClose } from '../../hooks/useBackToClose';
import {
  Button,
  Empty,
  Eyebrow,
  Field,
  FormError,
  Hint,
  INPUT_CLS,
  Pill,
  Spinner,
  Stat,
  Stats,
  Table,
  TEXTAREA_CLS,
} from '../../components/consoleUi';

/** One vocabulary for a platform payment's state, everywhere on these screens. */
const PAYMENT_TONE = { requested: 'pend', link_sent: 'pend', paid: 'soon', verified: 'ok', failed: 'late', cancelled: 'grey' };
/* How money reached the platform, when it did not come through this screen. */
const METHOD_LABEL = { cash: 'Cash', upi: 'UPI', bank: 'Bank transfer', razorpay: 'Razorpay' };
const PAYMENT_LABEL = {
  requested: 'to pay',
  link_sent: 'to pay',
  paid: 'awaiting verification',
  verified: 'verified',
  failed: 'not verified',
  cancelled: 'withdrawn',
};


/** Table head from labels — the Table atom expects a <tr>, not strings. */
function th(cols) {
  return (
    <tr>
      {cols.map((c, i) => (
        <th key={i}>{c}</th>
      ))}
    </tr>
  );
}

export function PortalShell() {
  const { portalId } = useParams();
  const { ownedPortals } = useAuth();
  const name = ownedPortals.find((p) => p.portalId === portalId)?.portalName ?? 'Your portal';
  const tabs = [
    [`/p/${portalId}`, 'Overview', 'grid'],
    [`/p/${portalId}/members`, 'Members', 'people'],
    [`/p/${portalId}/subscription`, 'Subscription', 'rupee'],
    [`/p/${portalId}/trace`, 'Trace', 'eye'],
  ];
  return (
    <ConsoleShell title={name} subtitle="ADMIN PORTAL" tabs={tabs}>
      <Outlet />
    </ConsoleShell>
  );
}

function useLoad(fn, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const reload = useCallback(() => {
    setError(null);
    fn().then(setData).catch((err) => setError(err?.message ?? 'Could not load.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    reload();
  }, [reload]);
  return { data, error, reload };
}

function LoadGate({ state, children }) {
  if (state.error) {
    return (
      <Empty title="Could not load">
        {state.error}{' '}
        <button type="button" className="underline" onClick={state.reload}>
          Try again
        </button>
      </Empty>
    );
  }
  if (!state.data) return <div className="flex justify-center py-16"><Spinner /></div>;
  return children(state.data);
}

/* ------------------------------------------------------------- overview -- */

/**
 * The "to expiry" counter.
 *
 * It used to print `${days}d`, which read as a glued-on unit beside three
 * plain integers, and went honestly wrong at the two moments the number
 * matters most: an expired subscription showed `-12d`, and the last day
 * showed `0d`. A countdown has to say what it means at zero and past it, so
 * the tail cases get words and the rest gets a number with a real unit.
 */
function expiryStat(subscription) {
  if (!subscription) return { value: '—', label: 'To expiry' };
  const days = subscription.daysToExpiry;
  const unit = (n, word) => (
    <>
      {n}
      <span className="ml-1.5 text-[15px] font-bold text-copy-label">{word}</span>
    </>
  );
  if (days < 0) {
    return { value: unit(Math.abs(days), days === -1 ? 'day over' : 'days over'), label: 'Expired', numberTone: 'flag' };
  }
  if (days === 0) return { value: 'Today', label: 'Expires', numberTone: 'flag' };
  return {
    value: unit(days, days === 1 ? 'day' : 'days'),
    label: 'To expiry',
    numberTone: days <= 30 ? 'amber' : undefined,
  };
}

export function PortalOverview() {
  const { portalId } = useParams();
  const { selectClub, refreshSession } = useAuth();
  const navigate = useNavigate();
  const state = useLoad(() => platform.portal(portalId), [portalId]);
  const [dialog, setDialog] = useState(null); // 'create' | {kind:'archive'|'notify', club}
  const [value, setValue] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function act(fn, { keepDialog = false } = {}) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (!keepDialog) setDialog(null);
      setValue({});
      state.reload();
      refreshSession().catch(() => {});
    } catch (err) {
      setError(err?.message ?? 'Could not do that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          {d.portal.accessState === 'grace' && (
            <Hint tone="warn">
              {d.subscription?.isTrial ? (
                <>
                  Your free trial has ended — the portal is READ-ONLY for{' '}
                  {d.portal.graceDaysLeft} more day{d.portal.graceDaysLeft === 1 ? '' : 's'}.
                  Nothing is deleted: choose a plan from the Subscription tab and
                  pick up where you left off.
                </>
              ) : (
                <>
                  The subscription has expired — the portal is READ-ONLY for{' '}
                  {d.portal.graceDaysLeft} more day{d.portal.graceDaysLeft === 1 ? '' : 's'}. Renew from
                  the Subscription tab to restore full access.
                </>
              )}
            </Hint>
          )}
          {d.subscription?.isTrial && d.portal.accessState === 'active' && (
            <Hint>
              Free trial — ends {formatDate(d.subscription.end)}. {d.seats.used} of{' '}
              {d.seats.capacity} member seats and {d.trial?.eventsUsed ?? 0} of{' '}
              {d.trial?.eventLimit ?? 20} events used; messages and everything else
              are unlimited. Choose a plan from the Subscription tab any time —
              everything carries over.
            </Hint>
          )}
          {d.seats.warning && (
            <Hint tone="warn">
              Seat utilisation is above 90% — {d.seats.free} seat
              {d.seats.free === 1 ? '' : 's'} left. Consider an upgrade before adding members.
            </Hint>
          )}

          {/* The plan's own door. It lived only on the Subscription tab, which
              a phone keeps behind the hamburger — the seat numbers were on
              screen with nothing to act on them. An open request goes
              straight to its pay desk instead of the ladder. */}
          <Eyebrow
            right={
              d.subscription ? (
                <span className="flex items-center gap-3">
                  <span>ends {formatDate(d.subscription.end)}</span>
                  <Button
                    small
                    tone="p"
                    onClick={() =>
                      navigate(
                        d.pendingPayments.length > 0
                          ? `/p/${portalId}/subscription`
                          : `/p/${portalId}/subscription?plans=1`,
                      )
                    }
                  >
                    {d.pendingPayments.length > 0
                      ? 'Finish payment'
                      : d.subscription.isTrial
                        ? 'Choose a plan'
                        : 'Upgrade'}
                  </Button>
                </span>
              ) : null
            }
          >
            {d.subscription ? `${d.subscription.plan} plan` : 'No subscription'}
          </Eyebrow>
          <Stats>
            <Stat value={d.seats.capacity} label="Seat capacity" />
            <Stat value={d.seats.used} label="Seats used" />
            <Stat value={d.seats.free} label="Seats free" numberTone={d.seats.warning ? 'flag' : 'good'} />
            <Stat {...expiryStat(d.subscription)} />
          </Stats>

          <div className="mt-8">
            <Eyebrow
              right={
                <Button small tone="p" onClick={() => { setDialog('create'); setError(null); }}>
                  New club
                </Button>
              }
            >
              Clubs
            </Eyebrow>
            {d.clubs.length === 0 ? (
              <Empty title="No clubs yet">Create your first club — it shares the portal's seats.</Empty>
            ) : (
              <div className="grid gap-px border border-line bg-line">
                {d.clubs.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 bg-white p-5">
                    <div>
                      <p className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">{c.name}</p>
                      <p className="font-market text-xs tabular-nums text-copy-label">
                        {c.seats} seat{c.seats === 1 ? '' : 's'}
                        {c.location ? ` · ${c.location}` : ''}
                      </p>
                      {c.status === 'archived' && (
                        <p className="mt-0.5 text-xs text-flag-600">
                          Archived {formatDate(c.archivedAt?.slice(0, 10))}
                          {c.restorable ? ' — restorable' : ' — the 10-day window has closed'}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {c.status === 'active' ? (
                        <>
                          <Pill tone="ok">active</Pill>
                          <Button
                            small
                            onClick={() => {
                              selectClub(c.id);
                              navigate('/');
                            }}
                          >
                            Open
                          </Button>
                          <Button small tone="r" onClick={() => { setDialog({ kind: 'archive', club: c }); setError(null); }}>
                            Archive
                          </Button>
                        </>
                      ) : (
                        <>
                          <Pill tone="grey">archived</Pill>
                          {c.restorable && (
                            <Button small tone="p" onClick={() => act(() => platform.restoreClub(portalId, c.id))}>
                              Restore
                            </Button>
                          )}
                          <Button small onClick={() => { setDialog({ kind: 'notify', club: c }); setError(null); }}>
                            Notify members
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {error && !dialog && <FormError>{error}</FormError>}
          </div>

          {d.pendingPayments.length > 0 && (
            <div className="mt-8">
              <Eyebrow
                right={
                  <Link
                    to={`/p/${portalId}/subscription`}
                    className="font-market text-[11px] font-bold uppercase tracking-[0.14em] text-sea-600 underline decoration-sea-600/30 underline-offset-[3px]"
                  >
                    Open subscription
                  </Link>
                }
              >
                Open payment requests
              </Eyebrow>
              <Table head={th(['Type', 'Amount', 'Status', 'Raised'])}>
                {d.pendingPayments.map((p) => (
                  <tr key={p.id}>
                    <td>{p.type}</td>
                    <td className="font-market tabular-nums">{formatMoney(p.amount)}</td>
                    <td><Pill tone={PAYMENT_TONE[p.status]}>{PAYMENT_LABEL[p.status] ?? p.status}</Pill></td>
                    <td className="font-market text-xs tabular-nums">{new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                  </tr>
                ))}
              </Table>
              <Hint>
                {d.pendingPayments.some((p) => p.status === 'paid')
                  ? 'Your payment screenshot is with the platform owner. The plan changes the moment it is verified.'
                  : 'Pay from the Subscription tab — it shows the bank details and QR, and takes your screenshot.'}
              </Hint>
            </div>
          )}

          {dialog === 'create' && (
            <Dialog title="New club" onClose={() => setDialog(null)}>
              <Field label="Club name" required>
                <input className={INPUT_CLS} value={value.name ?? ''} onChange={(e) => setValue((v) => ({ ...v, name: e.target.value }))} />
              </Field>
              <Field label="Location">
                <input className={INPUT_CLS} value={value.location ?? ''} onChange={(e) => setValue((v) => ({ ...v, location: e.target.value }))} />
              </Field>
              <Field label="Description">
                <textarea className={TEXTAREA_CLS} value={value.description ?? ''} onChange={(e) => setValue((v) => ({ ...v, description: e.target.value }))} />
              </Field>
              <Hint>Creating a club takes ONE seat — your own admin membership in it.</Hint>
              {error && <FormError>{error}</FormError>}
              <div className="mt-4 flex justify-end gap-2">
                <Button small onClick={() => setDialog(null)}>Cancel</Button>
                <Button small tone="p" disabled={busy} onClick={() => act(() => platform.createClub(portalId, value))}>
                  {busy ? 'Creating…' : 'Create club'}
                </Button>
              </div>
            </Dialog>
          )}

          {dialog?.kind === 'archive' && (
            <Dialog title={`Archive ${dialog.club.name}?`} onClose={() => setDialog(null)}>
              <p className="text-sm leading-relaxed text-copy-muted">
                The club disappears from every screen and every member's club
                selector, and its <strong>{dialog.club.seats} seats are released
                immediately</strong>. You can restore it within <strong>10 days</strong> —
                if enough seats are still free. After that it is permanently
                closed (records are kept).
              </p>
              {error && <FormError>{error}</FormError>}
              <div className="mt-4 flex justify-end gap-2">
                <Button small onClick={() => setDialog(null)}>Keep the club</Button>
                <Button small tone="r" disabled={busy} onClick={() => act(() => platform.archiveClub(portalId, dialog.club.id))}>
                  {busy ? 'Archiving…' : 'Archive club'}
                </Button>
              </div>
            </Dialog>
          )}

          {dialog?.kind === 'notify' && (
            <Dialog title={`Tell ${dialog.club.name}'s members`} onClose={() => setDialog(null)}>
              <p className="text-sm text-copy-muted">
                Members are not told automatically when a club is archived — you
                decide. This emails every member of the club once.
              </p>
              <Field label="A note to include (optional)">
                <textarea className={TEXTAREA_CLS} value={value.note ?? ''} onChange={(e) => setValue((v) => ({ ...v, note: e.target.value }))} />
              </Field>
              {error && <FormError>{error}</FormError>}
              <div className="mt-4 flex justify-end gap-2">
                <Button small onClick={() => setDialog(null)}>Cancel</Button>
                <Button small tone="p" disabled={busy} onClick={() => act(() => platform.notifyArchivedMembers(portalId, dialog.club.id, value.note ?? ''))}>
                  {busy ? 'Queueing…' : 'Email the members'}
                </Button>
              </div>
            </Dialog>
          )}
        </>
      )}
    </LoadGate>
  );
}

function Dialog({ title, children, onClose }) {
  useBackToClose(true, onClose);
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink-900/70 p-4 backdrop-blur-[2px] motion-safe:animate-fadein"
      role="dialog"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md border border-line bg-white p-5 shadow-[0_40px_80px_-40px_rgba(11,15,24,.55)]">
        <p className="mb-2 font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">{title}</p>
        {children}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- members -- */

export function PortalMembers() {
  const { portalId } = useParams();
  const state = useLoad(() => platform.portalMembers(portalId), [portalId]);
  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          <Eyebrow right={d.members.length}>Members across clubs</Eyebrow>
          <p className="mb-3 text-xs text-copy-label">
            One row per membership — the same person in two clubs is two rows
            and two seats (SRS seat rule).
          </p>
          {d.members.length === 0 ? (
            <Empty title="Nobody yet" />
          ) : (
            <Table head={th(['Member', 'Club', 'Role', 'Status', 'Seat'])}>
              {d.members.map((m) => (
                <tr key={m.membershipId}>
                  {/* ONE wrapper: on a phone the cell is a flex row, and two
                      loose children would sit as two separate items. */}
                  <td>
                    <span className="block">
                      <span className="font-semibold text-ink-700">{m.name}</span>
                      <span className="block font-market text-[10px] text-copy-label">{m.email}</span>
                      {m.invitePending && (
                        <span className="block text-[10px] text-gold-800">
                          invitation not yet accepted{m.inviteBounced ? ' · BOUNCED — fix the address and resend' : ''}
                        </span>
                      )}
                    </span>
                  </td>
                  <td>{m.clubName}</td>
                  <td>{m.role}</td>
                  <td><Pill tone={{ active: 'ok', pending: 'pend', paused: 'sent', left: 'grey' }[m.status] ?? 'grey'}>{m.status}</Pill></td>
                  <td className="font-market tabular-nums">{m.consumesSeat ? '1' : '0'}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </LoadGate>
  );
}

/* --------------------------------------------------------- subscription -- */

/**
 * The plan cards the owner picks from — the pricing page inside the product.
 * A trial portal lands here to leave the trial; a paid portal to grow. The
 * request itself is the EXISTING payment flow (request → payment link →
 * verify) — this screen only chooses the plan, it never takes money.
 */
function PlanPicker({ plans, current, isTrial, busy, onPick }) {
  const paid = plans.filter((p) => !p.isTrial);
  if (paid.length === 0) return null;
  // The gap IS the rule: size the grid to the plan count so the line-coloured
  // backing never shows through an unfilled column (see ui.jsx's Stats).
  const COLS = { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3' };
  const cols = paid.length % 3 === 0 ? 3 : paid.length % 2 === 0 ? 2 : Math.min(paid.length, 3);
  return (
    <div className={`grid gap-px border border-line bg-line ${COLS[cols]}`}>
      {paid.map((p) => {
        const isCurrent = !isTrial && current?.plan === p.name;
        const monthly = Math.round(Number(p.price) / 12);
        return (
          <div key={p.id} className="flex flex-col bg-white p-5">
            <p className="font-market text-[15px] font-extrabold uppercase tracking-[0.04em] text-ink-700">
              {p.name}
            </p>
            <p className="mt-1 font-market text-[24px] font-extrabold tabular-nums text-ink-700">
              ₹{Number(p.price).toLocaleString('en-IN')}
              <span className="ml-1 text-[12px] font-semibold text-copy-muted">/ year</span>
            </p>
            <p className="font-market text-xs tabular-nums text-copy-muted">
              ≈ ₹{monthly.toLocaleString('en-IN')}/month · {p.seats.toLocaleString('en-IN')} member
              seats · unlimited clubs
            </p>
            <div className="mt-4">
              {isCurrent ? (
                <Pill tone="ok">current plan</Pill>
              ) : (
                <Button small tone="p" disabled={busy} onClick={() => onPick(p)}>
                  {isTrial ? 'Upgrade to this plan' : 'Request this plan'}
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The pay desk — the platform's bank account, UPI id and QR, printed the
 * moment a payment request is open, with the screenshot upload under it.
 * The portal is the PAYER here: these are the platform owner's details from
 * the Super Admin's Bank details screen, never the portal's own.
 *
 * Same shape as the public /pay page's PaymentPanel (method tabs, Copy per
 * field, proof, one button) but its own component: the words differ on
 * every line, and the club panel's cash-at-the-ground escape hatch has no
 * meaning for a subscription.
 */
function PlatformPayDesk({ payment, bank, busy, onPaid }) {
  const methods = paymentMethods({
    upi: bank.upi,
    qrUrl: bank.qrUrl,
    holder: bank.holder,
    account: bank.account,
    ifsc: bank.ifsc,
  });
  const [tab, setTab] = useState(methods[0] ?? 'upi');
  const active = methods.includes(tab) ? tab : methods[0];
  const [proof, setProof] = useState(null);
  const [reference, setReference] = useState('');
  const [error, setError] = useState(null);
  const TAB_LABEL = { upi: 'UPI / QR', bank: 'Bank transfer' };

  async function send() {
    setError(null);
    if (!proof) return setError('Attach the payment screenshot first.');
    try {
      await onPaid(payment.id, proof, reference.trim());
    } catch (err) {
      setError(err?.message ?? 'Could not send your proof.');
    }
  }

  const what =
    payment.type === 'upgrade'
      ? `${payment.targetPlan ?? 'New'} plan${payment.targetSeats ? ` · ${payment.targetSeats.toLocaleString('en-IN')} seats` : ''}`
      : 'Renewal · one more year';

  return (
    <section className="border border-line bg-white">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-5 pb-4 pt-[18px]">
        <div>
          <h2 className="font-market text-[19px] font-extrabold tracking-[-0.025em] text-ink-700">
            Pay for your plan
          </h2>
          <p className="mt-1 font-market text-[11.5px] text-copy-muted">{what}</p>
        </div>
        <span className="font-market text-[26px] font-extrabold leading-none tabular-nums text-ink-700">
          {formatMoney(payment.amount)}
        </span>
      </div>

      {!bank.configured ? (
        <div className="border-l-2 border-gold-500 bg-gold-50 px-5 py-4">
          <p className="font-market text-[13.5px] font-extrabold tracking-[-0.02em] text-ink-700">
            The platform owner has not published bank details yet
          </p>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-copy-muted">
            Your request is logged. As soon as an account or QR is published it
            appears here — nothing is charged from this page.
          </p>
        </div>
      ) : (
        <>
          {methods.length > 1 && (
            <div className="flex gap-px border-b border-line bg-line">
              {methods.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setTab(m)}
                  data-label={TAB_LABEL[m]}
                  className={`text-stable flex-1 px-3 py-2.5 font-market text-[12px] uppercase tracking-[0.08em] transition-colors duration-200 ${
                    active === m ? 'bg-white font-bold text-ink-700' : 'bg-ground text-copy-muted hover:text-ink-700'
                  }`}
                >
                  {TAB_LABEL[m]}
                </button>
              ))}
            </div>
          )}
          <div className="px-5 py-4">
            {active === 'upi' ? (
              <>
                {bank.qrUrl && (
                  <div className="flex flex-col items-center pb-3">
                    <img
                      src={bank.qrUrl}
                      alt="Scan to pay RunnerCrew"
                      className="h-[188px] w-[188px] border border-line object-contain p-1"
                    />
                    <p className="mt-2 font-market text-[10px] uppercase tracking-[0.14em] text-copy-label">
                      Scan with any UPI app
                    </p>
                  </div>
                )}
                {bank.upi && <CopyRow label="UPI id" value={bank.upi} />}
              </>
            ) : (
              <>
                {bank.holder && <CopyRow label="Account name" value={bank.holder} />}
                {bank.account && <CopyRow label="Account number" value={bank.account} mono />}
                {bank.ifsc && <CopyRow label="IFSC" value={bank.ifsc} mono />}
              </>
            )}
            <p className="mt-3 border-l-2 border-gold-500 bg-ground px-3 py-2 text-[12px] leading-relaxed text-copy-muted">
              {bank.details || 'Put your portal name in the payment note so the platform owner can match it.'}
            </p>
          </div>

          <div className="border-t border-line bg-ground px-5 py-4">
            <p className="font-market text-[11px] uppercase tracking-[0.14em] text-copy-label">
              Then tell us you have paid
            </p>
            <ProofPicker
              proof={proof}
              onChange={setProof}
              audience="the platform owner"
              title="Attach the payment screenshot"
            />
            <div className="mt-3">
              <Field label="UTR / transaction reference (optional)">
                <input
                  className={INPUT_CLS}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="From your banking app"
                  maxLength={60}
                />
              </Field>
            </div>
            <button
              type="button"
              className="w-full bg-gold-500 p-4 font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-800 transition-all duration-200 hover:brightness-110 active:translate-y-px disabled:pointer-events-none disabled:opacity-60"
              onClick={() => void send()}
              disabled={busy || !proof}
            >
              {busy ? 'Sending your proof…' : `I have paid ${formatMoney(payment.amount)}`}
            </button>
            {error && <p className="mt-2.5 font-market text-[11px] text-flag-600">{error}</p>}
            <p className="mt-2 text-center font-market text-[9.5px] tracking-wide text-copy-label">
              The plan changes when the platform owner verifies the payment
            </p>
          </div>
        </>
      )}
    </section>
  );
}

/** The state of the one open payment, once proof is uploaded. */
function AwaitingVerification({ payment }) {
  return (
    <section className="flex flex-wrap items-center gap-4 border border-line bg-white p-5">
      {payment.proofUrl && (
        <a href={payment.proofUrl} target="_blank" rel="noreferrer" className="flex-none">
          <img
            src={payment.proofUrl}
            alt="Your payment screenshot"
            className="h-[96px] w-[72px] border border-line bg-ground object-cover"
          />
        </a>
      )}
      <div className="min-w-0 flex-1">
        <div className="mb-1.5"><Pill tone="soon">awaiting verification</Pill></div>
        <p className="font-market text-[17px] font-extrabold tracking-[-0.02em] text-ink-700">
          {formatMoney(payment.amount)} reported
          {payment.proofUploadedAt ? ` on ${formatDate(String(payment.proofUploadedAt).slice(0, 10))}` : ''}
        </p>
        <p className="mt-1 text-[12.5px] leading-relaxed text-copy-muted">
          {payment.reference ? <>Reference <b className="font-market text-ink-700">{payment.reference}</b>. </> : null}
          The platform owner checks it against the bank statement. Your{' '}
          {payment.type === 'upgrade' ? `${payment.targetPlan ?? 'new'} plan` : 'renewal'} starts the
          moment it is verified; you will get an email either way.
        </p>
      </div>
    </section>
  );
}

export function PortalSubscription() {
  const { portalId } = useParams();
  const subs = useLoad(() => platform.portalSubscription(portalId), [portalId]);
  const payments = useLoad(() => platform.portalPayments(portalId), [portalId]);
  const overview = useLoad(() => platform.portal(portalId), [portalId]);
  const plansState = useLoad(() => platform.plans(), []);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);
  const [busy, setBusy] = useState(false);
  // `?plans=1` — the Overview's Upgrade button lands with the ladder open.
  const [searchParams] = useSearchParams();
  const [showPlans, setShowPlans] = useState(searchParams.get('plans') === '1');

  const current = overview.data?.subscription ?? null;
  const isTrial = Boolean(current?.isTrial);
  const list = payments.data?.payments ?? [];
  const bank = payments.data?.bank ?? null;
  /* One open request at a time (the API enforces it): the one to pay, or
     the one being verified. */
  const toPay = list.find((p) => p.status === 'requested' || p.status === 'link_sent') ?? null;
  const awaiting = list.find((p) => p.status === 'paid') ?? null;
  /* Only when the MOST RECENT payment was the rejected one: an old rejection
     under a later verified payment is history, not a warning. */
  const lastRejected =
    !toPay && !awaiting && list[0]?.status === 'failed' && list[0].rejectionReason ? list[0] : null;

  async function request(type, planId) {
    if (busy) return;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await platform.requestPayment(portalId, type, planId);
      setOk('Request raised. Pay the amount below and attach your screenshot.');
      setShowPlans(false);
      payments.reload();
      overview.reload();
    } catch (err) {
      setError(err?.message ?? 'Could not raise the request.');
    } finally {
      setBusy(false);
    }
  }

  /* The way back out of the pay desk: withdraw the unpaid request and the
     plans come back. Without it a wrong tap left a bill on screen for good. */
  async function cancel(paymentId) {
    if (busy) return;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await platform.cancelPayment(portalId, paymentId);
      setOk('Request withdrawn. Nothing was charged.');
      setShowPlans(true);
      payments.reload();
      overview.reload();
    } catch (err) {
      setError(err?.message ?? 'Could not withdraw the request.');
    } finally {
      setBusy(false);
    }
  }

  async function paid(paymentId, proof, reference) {
    if (busy) return;
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await platform.uploadPaymentProof(portalId, paymentId, proof, reference);
      setOk('Proof sent. The platform owner verifies it and your plan updates.');
      payments.reload();
      overview.reload();
    } finally {
      setBusy(false);
    }
  }

  // The trial's ONLY exit is a paid plan, so the pricing is not behind a
  // button there — the cards are simply on the page. Not while a request is
  // open, though: the desk is the page then.
  const plansOpen = !toPay && !awaiting && (isTrial || showPlans);

  return (
    <LoadGate state={subs}>
      {(d) => (
        <>
          <Eyebrow
            right={
              <span className="flex gap-2">
                {!isTrial && !toPay && !awaiting && (
                  <>
                    <Button small onClick={() => setShowPlans((s) => !s)} disabled={busy}>
                      {showPlans ? 'Hide plans' : 'Request upgrade'}
                    </Button>
                    <Button small tone="p" onClick={() => request('renewal')} disabled={busy}>
                      Request renewal
                    </Button>
                  </>
                )}
              </span>
            }
          >
            Subscription
          </Eyebrow>
          {current && !isTrial && !toPay && !awaiting && (
            <Hint>
              Renewal always runs one year from your anniversary
              ({formatDate(current.end)}) — renewing early never
              shortens the term. An upgrade keeps the anniversary and changes
              only the seats.
            </Hint>
          )}
          {isTrial && !toPay && !awaiting && (
            <Hint>
              You are on the free trial until {formatDate(current.end)}. Choose a
              plan below, pay the platform&rsquo;s account or QR, and upload your
              screenshot — your paid year starts the day the platform owner
              verifies it, with everything intact.
            </Hint>
          )}
          {lastRejected && (
            <Hint tone="bad">
              Your last payment of {formatMoney(lastRejected.amount)} was not verified:
              &ldquo;{lastRejected.rejectionReason}&rdquo;. Raise the request again with the
              correct payment.
            </Hint>
          )}
          {ok && <Hint tone="ok">{ok}</Hint>}
          {error && <FormError>{error}</FormError>}

          {toPay && bank && (
            <div className="mb-8 mt-4">
              <PlatformPayDesk payment={toPay} bank={bank} busy={busy} onPaid={paid} />
              {/* Only the owner's own request — a link the platform owner sent
                  is theirs to withdraw (the API refuses it too). */}
              {toPay.requestedBy === 'admin' && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border border-line bg-white px-5 py-3">
                  <p className="text-[12.5px] leading-relaxed text-copy-muted">
                    Picked the wrong plan, or not ready to pay? Withdraw this
                    request — nothing is charged.
                  </p>
                  <Button small disabled={busy} onClick={() => void cancel(toPay.id)}>
                    {busy ? 'Withdrawing…' : isTrial ? 'Choose another plan' : 'Cancel request'}
                  </Button>
                </div>
              )}
            </div>
          )}
          {awaiting && (
            <div className="mb-8 mt-4">
              <AwaitingVerification payment={awaiting} />
            </div>
          )}

          {plansOpen && (
            <div className="mb-8">
              <Eyebrow>{isTrial ? 'Choose your plan' : 'Upgrade to'}</Eyebrow>
              <PlanPicker
                plans={plansState.data?.plans ?? []}
                current={current}
                isTrial={isTrial}
                busy={busy}
                onPick={(p) => request('upgrade', p.id)}
              />
            </div>
          )}

          <Table head={th(['Plan', 'Seats', 'Term', 'Type', 'Status'])}>
            {d.subscriptions.map((s) => (
              <tr key={s.id}>
                <td>{s.plan}</td>
                <td className="font-market tabular-nums">{s.capacity}</td>
                <td className="font-market tabular-nums">{formatDate(s.start)} → {formatDate(s.end)}</td>
                <td>{s.changeType}</td>
                <td><Pill tone={s.status === 'active' ? 'ok' : 'grey'}>{s.status}</Pill></td>
              </tr>
            ))}
          </Table>

          <div className="mt-8">
            <Eyebrow>Payments</Eyebrow>
            {list.length === 0 ? (
              <Empty title="No platform payments yet" />
            ) : (
              <Table head={th(['For', 'Amount', 'Status', 'Proof', 'Invoice', 'Date'])}>
                {list.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span className="block">
                        {p.type === 'upgrade' && p.targetPlan ? `${p.targetPlan} plan` : p.type}
                        {/* A payment with a method on it never passed through
                            this screen — the platform owner recorded money you
                            handed over directly. Say so, or it looks like a row
                            that appeared from nowhere. */}
                        {p.method && (
                          <span className="block font-market text-[10px] uppercase tracking-[0.1em] text-copy-label">
                            {METHOD_LABEL[p.method] ?? p.method} · recorded by the platform owner
                          </span>
                        )}
                        {p.status === 'failed' && p.rejectionReason && (
                          <span className="block text-[11px] text-flag-600">{p.rejectionReason}</span>
                        )}
                      </span>
                    </td>
                    <td className="font-market tabular-nums">{formatMoney(p.amount)}</td>
                    <td><Pill tone={PAYMENT_TONE[p.status]}>{PAYMENT_LABEL[p.status] ?? p.status}</Pill></td>
                    <td>
                      {p.proofUrl ? (
                        <a href={p.proofUrl} target="_blank" rel="noreferrer">
                          <img src={p.proofUrl} alt="Payment screenshot" className="h-10 w-8 border border-line object-cover" />
                        </a>
                      ) : (
                        <span className="text-copy-label">—</span>
                      )}
                    </td>
                    <td className="font-market">{p.invoiceNumber ?? '—'}</td>
                    <td className="font-market tabular-nums">{p.date ? formatDate(p.date) : '—'}</td>
                  </tr>
                ))}
              </Table>
            )}
          </div>
        </>
      )}
    </LoadGate>
  );
}

/* ---------------------------------------------------------------- trace -- */

export function PortalTrace() {
  const { portalId } = useParams();
  const state = useLoad(() => platform.portalTrace(portalId), [portalId]);
  return (
    <LoadGate state={state}>
      {(d) => (
        <>
          <Eyebrow right={d.entries.length}>Portal trace</Eyebrow>
          {d.entries.length === 0 ? (
            <Empty title="Nothing logged yet" />
          ) : (
            <Table head={th(['When', 'Actor', 'Action', 'Seats'])}>
              {d.entries.map((e) => (
                <tr key={e.id}>
                  <td className="whitespace-nowrap font-market text-xs tabular-nums">{new Date(e.at).toLocaleString('en-IN')}</td>
                  <td>{e.actor ?? '—'}</td>
                  <td className="font-market text-xs">{e.action}</td>
                  <td className="font-market tabular-nums">{e.seatsAffected ?? ''}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </LoadGate>
  );
}
